import { fromNumericKind, snapToStep, wordFor, type NumericKind } from '@anytracker/core'
import { useId, useState, type ChangeEvent, type CSSProperties } from 'react'

import { trackColour, trackFill } from '../../charts/colours.js'
import styles from './NumericInput.module.css'
import type { ValueInputProps } from './types.js'
import { ICONS, Icon } from '../Layout.js'

interface Props extends ValueInputProps {
  readonly kind: NumericKind
}

/**
 * A slider with a large readout, plus a number field for precision.
 *
 * The snapping rule is `snapToStep` from core, not arithmetic here. A slider
 * that produced 0.30000000000000004, or a value between two steps, would store
 * it forever — and the same rule has to apply identically on native, so it is
 * domain logic rather than a display concern.
 */
export function NumericInput({
  track,
  kind,
  value,
  onChange,
  disabled = false,
  labelledBy,
  describedBy,
  variant = 'compact',
}: Props) {
  const scale = fromNumericKind(kind)
  const numeric = typeof value === 'number' ? value : null
  const preciseId = useId()

  /**
   * What the number field shows while being typed in.
   *
   * Held separately because snapping every keystroke makes the field
   * unusable — typing "7" toward "7.5" would jump to 7 and eat the decimal
   * point. Snap on blur instead. `null` means "show the committed value".
   */
  const [typing, setTyping] = useState<string | null>(null)

  // The slider needs a position even with nothing recorded. It starts at the
  // midpoint, which reads as "unset" far better than the minimum does — a
  // hunger slider parked at 1 looks like an answer.
  const sliderValue = numeric ?? snapToStep((scale.min + scale.max) / 2, scale)

  function handleSlider(event: ChangeEvent<HTMLInputElement>) {
    onChange(snapToStep(Number(event.target.value), scale))
  }

  function commitTyped() {
    if (typing === null) {
      return
    }
    const trimmed = typing.trim()
    setTyping(null)
    if (trimmed === '') {
      onChange(null)
      return
    }
    const parsed = Number(trimmed)
    if (Number.isFinite(parsed)) {
      onChange(snapToStep(parsed, scale))
    }
  }

  const displayed = numeric === null ? '—' : numeric.toFixed(scale.decimals)

  /*
   * The word for this point on the scale, where the author gave one (T-807).
   *
   * The scale is theirs, so only they can say what 8 means. Once they have,
   * this is the difference between a control that reports a number and one that
   * reports an answer — and for anyone using a screen reader it is most of the
   * information, which is why it goes into `aria-valuetext` rather than only on
   * screen.
   */
  const word = numeric === null ? null : wordFor(kind, numeric)

  /*
   * How far along the rail the fill reaches, as a percentage.
   *
   * **Nothing when unmeasured.** The prototype fills to the midpoint before
   * anything is recorded, which draws a half-full bar for a value nobody chose
   * — the readout says "not measured" while the rail says fifty percent. The
   * thumb still rests mid-rail, because it has to be somewhere and the minimum
   * would read as a low answer; the *fill* is what claims a value, so there
   * isn't one.
   */
  const fill =
    numeric === null ? 0 : ((numeric - scale.min) / (scale.max - scale.min)) * 100

  /*
   * One step either way, from the full variant's − and + buttons.
   *
   * From nothing recorded they step from the resting midpoint, so the first
   * press is a deliberate answer one step off centre rather than a jump to an
   * end. Pressing is a choice; resting is not, and still records nothing.
   */
  function nudge(direction: 1 | -1) {
    const base = numeric ?? sliderValue
    onChange(snapToStep(base + direction * scale.step, scale))
  }

  const full = variant === 'full'
  const minWord = full ? wordFor(kind, scale.min) : null
  const maxWord = full ? wordFor(kind, scale.max) : null

  const readout = (
    <div className={styles['readout']}>
      <span
        className={`${styles['value']} ${numeric === null ? styles['unmeasured'] : ''}`}
        // The slider already announces its value; a second live reading of
        // the same number would double up on every drag.
        aria-hidden="true"
      >
        {displayed}
      </span>
      {track.unit !== undefined && <span className={styles['unit']}>{track.unit}</span>}
      {/*
          Beside the number, and `aria-hidden` for the same reason the number
          is: the slider already announces both through `aria-valuetext`, and a
          second live reading would double up on every drag.
        */}
      {word !== null && (
        <span className={styles['word']} aria-hidden="true">
          {word}
        </span>
      )}
      {full && numeric === null && (
        <span className={styles['word']} aria-hidden="true">
          not measured
        </span>
      )}
    </div>
  )

  return (
    <div
      className={`${styles['root']} ${full ? styles['full'] : ''}`}
      // The track's own colour, so the number and the fill read as this track.
      style={
        {
          '--track-deep': trackColour(track),
          '--track-fill': trackFill(track),
        } as CSSProperties
      }
    >
      {full ? (
        <div className={styles['stepper']}>
          <button
            type="button"
            className={styles['step']}
            aria-label={`Decrease ${track.name}`}
            disabled={disabled || numeric === scale.min}
            onClick={() => {
              nudge(-1)
            }}
          >
            <Icon path={ICONS.minus} />
          </button>
          {readout}
          <button
            type="button"
            className={styles['step']}
            aria-label={`Increase ${track.name}`}
            disabled={disabled || numeric === scale.max}
            onClick={() => {
              nudge(1)
            }}
          >
            <Icon path={ICONS.plus} />
          </button>
        </div>
      ) : (
        readout
      )}

      <input
        type="range"
        className={styles['slider']}
        /*
         * The fill is a gradient stop, not a second element.
         *
         * The prototype draws the rail as a `<div onPointerDown>` with three
         * absolutely-positioned children, which looks identical and cannot be
         * operated from a keyboard at all — no arrows, no Home, no End, no
         * `role="slider"`. This stays a native range input and paints the fill
         * through a custom property.
         */
        style={{ ['--fill']: `${String(fill)}%` } as CSSProperties}
        min={scale.min}
        max={scale.max}
        step={scale.step}
        value={sliderValue}
        disabled={disabled}
        onChange={handleSlider}
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        // Without this a screen reader reads the raw number. With it, an
        // unmeasured track says so rather than announcing the midpoint as
        // though it were a recorded value.
        aria-valuetext={
          numeric === null
            ? 'Not measured'
            : [`${displayed}${track.unit === undefined ? '' : ` ${track.unit}`}`, word]
                .filter((part) => part !== null && part !== '')
                .join(', ')
        }
      />

      <div className={styles['bounds']} aria-hidden="true">
        <span>
          {scale.min}
          {minWord === null ? '' : ` · ${minWord}`}
        </span>
        <span>
          {scale.max}
          {maxWord === null ? '' : ` · ${maxWord}`}
        </span>
      </div>

      <div className={styles['precise']}>
        <label className={styles['preciseLabel']} htmlFor={preciseId}>
          Exact
        </label>
        <input
          id={preciseId}
          type="number"
          inputMode="decimal"
          className={styles['preciseField']}
          min={scale.min}
          max={scale.max}
          step={scale.step}
          disabled={disabled}
          value={typing ?? (numeric === null ? '' : String(numeric))}
          onChange={(event) => setTyping(event.target.value)}
          onBlur={commitTyped}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              commitTyped()
            }
          }}
          aria-describedby={describedBy}
        />
      </div>
    </div>
  )
}
